import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  computed,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlsOf, FormArray, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, Subject, debounceTime, map, merge, take } from 'rxjs';
import {
  FormQuestion,
  FormSection,
} from '../../core/models/form-group-mapping.model';
import { FormType } from '../../core/models/form.model';
import { QuestionType } from '../../core/models/question/question-type.model';
import { Section } from '../../core/models/section.model';
import { RendererFormsQuery } from '../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../core/repositories/renderer-forms.repository';
import { RendererSectionsRepository } from '../../core/repositories/renderer-sections.repository';
import { FormValidationService } from '../../core/services/form-validation.service';

@UntilDestroy()
@Component({
  selector: 'lib-section',
  templateUrl: './section.component.html',
  styleUrls: ['./section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SectionComponent implements OnInit {
  private opened = new Subject<void>();

  section: Section;
  sectionFormGroup = input<FormGroup<ControlsOf<FormSection>>>();
  isCollapsed = input<boolean>(true);
  showAllFollowUps = input<boolean>();
  hideValidation = input<boolean>();
  previewMode = input<boolean>(false);

  sectionStatus$: Observable<SectionStatus>;
  showSubTitle: boolean;
  bodyWidth: 'small' | 'medium' | 'full' = 'medium';

  hideSectionNumbers: boolean;
  questions: Signal<FormGroup<ControlsOf<FormQuestion>>[]>;
  activeLang: Signal<string>;
  renderSection: Signal<boolean>;

  sectionNumber: string;

  SectionStatus = SectionStatus;

  get disabled(): boolean {
    return this.sectionFormGroup().disabled;
  }

  constructor(
    private rendererSectionsRepository: RendererSectionsRepository,
    private formsQuery: RendererFormsQuery,
    private translocoService: TranslocoService,
    private formValidationService: FormValidationService,
    private rendererFormsRepository: RendererFormsRepository,
  ) {
    this.questions = computed(() => {
      const questions = [];
      for (const question of (
        this.sectionFormGroup().get('questions') as FormArray<FormSection>
      ).controls) {
        questions[question.value.id] = question;
      }
      return questions;
    });

    this.activeLang = toSignal(this.translocoService.langChanges$);
    this.renderSection = toSignal(
      this.formValidationService.renderAllSections$,
    );
  }

  ngOnInit() {
    this.section = this.rendererSectionsRepository.get(
      this.sectionFormGroup().value.id,
    );

    this.sectionNumber =
      this.formsQuery.getSectionNumber(this.section.id) ?? '';

    this.sectionStatus$ = merge(
      this.formValidationService.sectionInvalid(this.section.id).pipe(
        debounceTime(0),
        map((invalid) => {
          if (invalid) {
            this.showSubTitle = true;
            return SectionStatus.Invalid;
          } else if (
            !invalid &&
            this.sectionFormGroup().dirty &&
            this.sectionFormGroup().valid
          ) {
            this.showSubTitle = true;
            return SectionStatus.Valid;
          } else {
            return SectionStatus.NoShow;
          }
        }),
      ),
      this.opened.pipe(
        take(1),
        map(() => {
          if (this.sectionFormGroup().valid) {
            this.showSubTitle = true;
            return SectionStatus.Valid;
          } else if (this.showSubTitle) {
            return SectionStatus.Invalid;
          } else {
            return SectionStatus.NoShow;
          }
        }),
      ),
    );

    if (
      this.section.questions.some(
        (q) =>
          q.type === QuestionType.Worksite ||
          (q.type === QuestionType.Budget &&
            this.rendererFormsRepository.getActive().type !== FormType.Skiss),
      )
    ) {
      this.bodyWidth = 'full';
    } else if (
      this.section.questions.some((q) => q.type === QuestionType.FileUpload)
    ) {
      this.bodyWidth = 'small';
    }
  }

  onCollapse(isOpen) {
    if (isOpen) {
      this.opened.next();
    }
  }
}

enum SectionStatus {
  Valid,
  Invalid,
  NoShow,
}
